import React from 'react';
import styled from 'styled-components';

const BrowserShell = styled.div`
  background: #0f172a;
  border-radius: 16px;
  padding: 10px 10px 0;
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  width: 100%;
`;

const BrowserBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px 12px;
`;

const TrafficLight = styled.span<{ color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${props => props.color};
`;

const BrowserAddress = styled.div`
  flex: 1;
  margin-left: 8px;
  background: rgba(255, 255, 255, 0.08);
  color: #94a3b8;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  border-radius: 6px;
  padding: 4px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BrowserScreen = styled.img<{ ratio?: string }>`
  display: block;
  width: 100%;
  aspect-ratio: ${props => props.ratio || '16 / 10'};
  object-fit: cover;
  object-position: top left;
  background: #f8fafc;
  border-radius: 8px 8px 0 0;
`;

const PhoneShell = styled.div<{ large?: boolean }>`
  width: ${props => props.large ? '280px' : '100%'};
  max-width: 100%;
  background: #0f172a;
  border-radius: 36px;
  padding: 12px 10px 14px;
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.35);
  position: relative;
`;

const PhoneNotch = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  width: 36%;
  height: 14px;
  background: #0f172a;
  border-radius: 0 0 12px 12px;
  z-index: 2;
`;

const PhoneScreen = styled.img`
  display: block;
  width: 100%;
  border-radius: 26px;
  background: #f8fafc;
`;

export const BrowserFrame: React.FC<{
  src: string;
  alt: string;
  ratio?: string;
  address?: string;
}> = ({ src, alt, ratio, address = 'nexiserp.web.app' }) => (
  <BrowserShell>
    <BrowserBar>
      <TrafficLight color="#ff5f57" />
      <TrafficLight color="#febc2e" />
      <TrafficLight color="#28c840" />
      <BrowserAddress>{address}</BrowserAddress>
    </BrowserBar>
    <BrowserScreen src={src} alt={alt} ratio={ratio} />
  </BrowserShell>
);

export const PhoneFrame: React.FC<{
  src: string;
  alt: string;
  large?: boolean;
}> = ({ src, alt, large }) => (
  <PhoneShell large={large}>
    <PhoneNotch />
    <PhoneScreen src={src} alt={alt} />
  </PhoneShell>
);

const HeroStage = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 2.5rem;
`;

const HeroPhone = styled.div`
  position: absolute;
  right: -6%;
  bottom: -8%;
  width: 34%;
  max-width: 210px;
  z-index: 4;

  @media (max-width: 720px) {
    position: relative;
    right: auto;
    bottom: auto;
    width: 180px;
    margin: -3rem auto 0;
  }
`;

export const HeroProductStage: React.FC = () => (
  <HeroStage>
    <BrowserFrame
      src="/screenshots/Manager-Dashboard.png"
      alt="Nexis ERP manager dashboard"
    />
    <HeroPhone>
      <PhoneFrame
        src="/screenshots/Mobile-Stores.png"
        alt="Nexis ERP mobile store list"
      />
    </HeroPhone>
  </HeroStage>
);

const SplitStage = styled.div`
  position: relative;
  width: 100%;
`;

const SplitPhone = styled.div`
  position: absolute;
  left: -8%;
  bottom: -10%;
  width: 36%;
  max-width: 220px;
  z-index: 4;

  @media (max-width: 720px) {
    position: relative;
    left: auto;
    bottom: auto;
    width: 180px;
    margin: 1.5rem auto 0;
  }
`;

export const CustomizationProductStage: React.FC = () => (
  <SplitStage>
    <BrowserFrame
      src="/screenshots/Manager-Store-Management-.png"
      alt="Nexis ERP store management"
    />
    <SplitPhone>
      <PhoneFrame
        src="/screenshots/mobile-notes-.png"
        alt="Nexis ERP mobile store visit notes"
      />
    </SplitPhone>
  </SplitStage>
);

const FeaturedWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const FeaturedDesktop = styled.div`
  width: 100%;
  max-width: 920px;
`;

const FeaturedMobile = styled.div`
  width: min(280px, 70vw);
`;

const ThumbRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ThumbButton = styled.button<{ $active: boolean; $mobile: boolean }>`
  appearance: none;
  border: 2px solid ${props => props.$active ? '#1a428a' : '#e2e8f0'};
  background: white;
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  box-shadow: ${props => props.$active ? '0 10px 24px rgba(26, 66, 138, 0.18)' : '0 4px 12px rgba(15, 23, 42, 0.06)'};
  transform: translateY(${props => props.$active ? '-2px' : '0'});
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;

  &:hover {
    border-color: #1a428a;
  }

  img {
    display: block;
    width: 100%;
    height: ${props => props.$mobile ? '140px' : '92px'};
    object-fit: ${props => props.$mobile ? 'contain' : 'cover'};
    object-position: top left;
    background: #f8fafc;
  }
`;

const ThumbCaption = styled.div`
  padding: 0.7rem 0.8rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #1a365d;
`;

export type ProductShot = {
  title: string;
  description: string;
  image: string;
  icon: string;
  kind: 'desktop' | 'mobile';
};

export const ProductShowcase: React.FC<{
  items: ProductShot[];
  activeIndex: number;
  onSelect: (index: number) => void;
}> = ({ items, activeIndex, onSelect }) => {
  const featured = items[activeIndex] || items[0];
  if (!featured) return null;

  return (
    <div>
      <FeaturedWrap>
        {featured.kind === 'mobile' ? (
          <FeaturedMobile>
            <PhoneFrame src={featured.image} alt={featured.title} large />
          </FeaturedMobile>
        ) : (
          <FeaturedDesktop>
            <BrowserFrame src={featured.image} alt={featured.title} />
          </FeaturedDesktop>
        )}
      </FeaturedWrap>
      <ThumbRow style={{ gridTemplateColumns: `repeat(${Math.min(items.length, 4)}, 1fr)` }}>
        {items.map((item, index) => (
          <ThumbButton
            key={item.image}
            type="button"
            $active={index === activeIndex}
            $mobile={item.kind === 'mobile'}
            onClick={() => onSelect(index)}
            aria-pressed={index === activeIndex}
            aria-label={`Show ${item.title}`}
          >
            <img src={item.image} alt="" />
            <ThumbCaption>{item.title}</ThumbCaption>
          </ThumbButton>
        ))}
      </ThumbRow>
    </div>
  );
};
